<template>
  <section v-if="recommended_items.length" :id="$options.name" class="recommended">
    <div class="recommended__wrap">
      <div class="recommended__head">
        <p class="recommended__title">Soluzione consigliata</p>
        <p class="recommended__subtitle">{{ solution_label }}</p>
      </div>

      <div class="recommended__list">
        <vb-card
          v-for="(item, index) in recommended_items"
          :key="_uid + '_recommended_' + index"
          class="recommended__element"
          variant="horizontal"
          :card="{ image: room_image(item.room) }"
          @click_image="open_modal_recommended(item.product.id)"
        >
          <div class="recommended__row">
            <div class="recommended__main">
              <p class="recommended__name" @click="open_modal_recommended(item.product.id)">
                {{ item.room.name.it }}
              </p>
              <occupancy-product
                class="recommended__pax"
                :maximum_occupancy="item.product.maximum_occupancy"
              />
              <div class="recommended__details">
                <mealplan
                  v-if="item.product.policies.meal_plan.meals.length"
                  :meals="item.product.policies.meal_plan.meals"
                />
                <refundability :cancellation="item.product.policies.cancellation" />
                <div class="recommended__beds">
                  <vb-icon class="recommended__beds-icon" :name="'doublebed'" :color="sass('gray-900')" :size="13" />
                  <span v-html="format_beds(item.room.bed_options)"></span>
                </div>
              </div>
            </div>
            <div class="recommended__price">
              <span class="recommended__price-value">€ {{ item_price(item) }}</span>
              <span class="recommended__price-tax">include gestione e assistenza</span>
            </div>
          </div>
        </vb-card>
      </div>

      <div class="recommended__footer" :class="{ 'recommended__footer--plain': single_item }">
        <vb-button
          :vb_options="{ text: cta_label, variant: 'info' }"
          @click="select_recommended"
        />
      </div>
    </div>

    <vb-modal
      v-for="(item, index) in modal_items"
      :key="_uid + '_recommended_modal_' + index"
      :vb_options="{
        id: 'modal_room_recommended_' + item.product.id,
        body_class: 'rooms__modal',
        title: item.room.name.it,
        size: 'lg',
        centered: true,
        hide_footer: true
      }"
    >
      <room-modal :room="item.room" />
    </vb-modal>
  </section>
</template>

<script>
import { format_beds, markup, round_up } from '@bookingv3/utilities'
import RoomModal from '@bookingv3/components/blocks/detail/room/room-modal'
import OccupancyProduct from '@bookingv3/components/blocks/occupancy-product'
import Mealplan from '@bookingv3/components/atoms/mealplan'
import Refundability from '@bookingv3/components/atoms/refundability'

export default {
  name: 'recommended',

  components: {
    'room-modal': RoomModal,
    'occupancy-product': OccupancyProduct,
    'mealplan': Mealplan,
    'refundability': Refundability,
  },

  props: {
    details: Object,
    availabilities: Object,
  },

  data() {
    return {
    }
  },

  computed: {

    recommended_items() {
      const rec = this.availabilities && this.availabilities.recommendation
      if (!rec || !rec.products || !rec.products.length) return []

      return rec.products.map(rp => {
        const product = this.availabilities.products.find(p => p.id === rp.id)
        if (!product) return null
        const room = this.details.rooms.find(r => r.id === product.room)
        return room ? { room, product, rec: rp } : null
      }).filter(Boolean)
    },

    // Prodotti consigliati con la relativa quantità: lo stesso prodotto può essere
    // consigliato più volte (una occorrenza per camera)
    recommended_products() {
      return this.recommended_items.reduce((products, item) => {
        const found = products.find(p => p.product_id === item.product.id)
        if (found) found.qty += 1
        else products.push({ room_id: item.room.id, product_id: item.product.id, qty: 1 })
        return products
      }, [])
    },

    // Una sola modale per prodotto, per evitare id duplicati
    modal_items() {
      return this.recommended_products.map(p =>
        this.recommended_items.find(item => item.product.id === p.product_id)
      )
    },

    // Con una sola camera consigliata il footer è la cta di quell'unica opzione
    single_item() {
      return this.recommended_items.length === 1
    },

    cta_label() {
      return this.single_item ? 'Scegli' : 'Scegli le opzioni selezionate'
    },

    // Riepilogo della soluzione, es. "2 camere per 4 adulti e 1 bambino"
    solution_label() {
      const rooms = this.recommended_items.length
      const adults = this.recommended_items.reduce((tot, { rec }) => tot + (rec.number_of_adults || 0), 0)
      const children = this.recommended_items.reduce((tot, { rec }) => tot + ((rec.children && rec.children.length) || 0), 0)

      let label = `${rooms} ${rooms === 1 ? 'camera' : 'camere'} per ${adults} ${adults === 1 ? 'adulto' : 'adulti'}`
      if (children) label += ` e ${children} ${children === 1 ? 'bambino' : 'bambini'}`
      return label
    },

  },

  created() {
    this.format_beds = format_beds
  },

  methods: {

    room_image(room) {
      return (room.photos && room.photos.length)
        ? room.photos[0].url.standard
        : '/assets/images/common/statics/room_placeholder.png'
    },

    item_price(item) {
      return round_up(markup(item.product.price.total_tax_free).live_price)
    },

    open_modal_recommended(id) {
      this.$bvModal.show('modal_room_recommended_' + id)
    },

    // Preseleziona tutti i prodotti della soluzione consigliata e apre le camere coinvolte
    select_recommended() {
      this.recommended_products.forEach(product => {
        window.eventBus.$emit('recommended:preselect_product', product)
      })

      // apertura e scroll sono gestiti da 'rooms', che conosce l'ordine della lista disponibilità
      window.eventBus.$emit(
        'recommended:open_rooms',
        this.recommended_products.map(p => p.room_id)
      )
    },

  },
}
</script>

<style lang="scss" scoped>

  .recommended {
    @extend %section;
    @extend %section_margin;

    &__wrap {
      background-color: $white;
      border-radius: $border-radius-lg * 2;
      overflow: hidden;
    }

    &__head {
      background-color: #fafcff;
      padding: $grid-gutter-20;
      line-height: 1.25;
    }

    &__title {
      color: $primary;
      font-weight: $vb-font-weight-semibold;
      font-size: $font-size-xl;
      margin-bottom: 5px;
    }

    &__subtitle {
      font-size: $font-size-base;
      color: $dark;
      margin-bottom: 0;
    }

    &__list {
      // le colonne interne di vb-card aggiungono i propri 15px di padding
      padding: $grid-gutter-20 5px $grid-gutter-20;
    }

    &__element {
      & + & {
        margin-top: $grid-gutter-20;
      }
    }

    &__row {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 15px;
      width: 100%;
    }

    &__main {
      flex: 1 1 240px;
      min-width: 0;
    }

    &__name {
      font-size: $font-size-base;
      font-weight: $vb-font-weight-semibold;
      text-decoration: underline;
      margin-bottom: 5px;
      cursor: pointer;

      &:hover {
        color: $primary;
      }
    }

    &__pax {
      margin-bottom: 20px;
      font-size: $font-size-md;
    }

    &__details {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 14px;
    }

    &__beds {
      display: flex;
      align-items: center;
      font-size: $font-size-md;
      color: $gray-900;

      &-icon {
        margin-right: 5px;
      }
    }

    &__price {
      display: flex;
      flex-direction: column;
      text-align: right;
      margin-left: auto;
      align-self: flex-end;

      &-value {
        font-size: $font-size-lg;
        color: $primary;
        font-weight: $vb-font-weight-semibold;
      }

      &-tax {
        font-size: $font-size-md;
        color: $gray-500;
        line-height: normal;
      }
    }

    &__footer {
      display: flex;
      justify-content: flex-end;
      border-top: 1px solid $gray-200;
      padding: $grid-gutter-20 0;
      margin: 0 $grid-gutter-20;

      // con una sola opzione non c'è nulla da separare
      &--plain {
        border-top: 0;
        padding-top: 0;
      }
    }

    ::v-deep {

      // la b-row interna alla card non deve sfondare il padding del contenitore
      .vb-card__wrapper {
        margin-left: 0;
        margin-right: 0;
      }

      .vb-card__wrap {
        display: flex;
        align-items: center;
        border: 1px solid $gray-200;
        padding-top: 10px;
        padding-bottom: 10px;
      }

      .vb-card__image {
        cursor: pointer;
      }

      // l'angolo concavo dell'immagine va riempito col fondo della card, non con quello della pagina
      .vb-card__image-corner:before {
        box-shadow: 50px 50px 0 0 $white;
      }

      .vb-card__element--image {
        @include media-breakpoint-up(xl){
          flex: 0 0 20%;
          max-width: 20%;
        }
      }

      .vb-card__element--wrap {
        @include media-breakpoint-up(xl){
          flex: 0 0 80%;
          max-width: 80%;
        }
      }
    }
  }

</style>
